import React, { useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/it";
import {
  ArrowLeft,
  Calendar,
  CalendarClock,
  Download,
  Eye,
  GraduationCap,
  Link2,
  Pencil,
  Trash2,
  Users,
} from "lucide-react";
import AnimationFadeIn from "@components/AnimationFadeIn";
import Breadcrumb from "@components/Breadcrumb";
import TabsCst from "@components/TabsCst";
import EnrollmentListShell from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/components/EnrollmentListShell";
import { DetailAccordion } from "@pages/Contents/V2/Corporate/UsersGroups/components/detailLayout";
import { useActivityInfo } from "./hooks/useActivityInfo";
import { useEditionDetail } from "./hooks/useEditionDetail";
import {
  getEditionUserId,
  getEditionUserName,
  useEditionEnrolledUsers,
} from "./hooks/useEditionEnrolledUsers";
import { useEditionSessions } from "./hooks/useEditionSessions";
import SessionCard from "./components/SessionCard";
import RegistryTab from "./components/RegistryTab";
import SessionDetailModal, {
  DetailTab as SessionDetailTab,
} from "./components/SessionDetailModal";
import { openEditionEnrollmentModal } from "./EditionEnrollment/host";
import { openSessionWizardModal } from "./SessionWizard/host";
import { useSessionWizardSubmit } from "./SessionWizard/hooks/useSessionWizardSubmit";
import { useDeleteSession } from "./SessionWizard/hooks/useDeleteSession";
import {
  PermissionGate,
  PERMISSIONS,
  editionPerm,
  usePermissions,
} from "@utils/permissions";
import { openToast } from "@utils/toastStore";
import { EditionKind, EditionSession, EditionEnrolledUser } from "./types";
import { useDeleteEditionEnrollment } from "./hooks/useDeleteEditionEnrollment";
import { useDownloadEditionEnrolledUsers } from "./hooks/useDownloadEditionEnrolledUsers";
import { useDeleteEdition } from "./hooks/useDeleteEdition";
import { useEditionMailto } from "./hooks/useEditionMailto";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import {
  ServicesURL,
  formatSessionOccupiedError,
  getAllErrorsAsString,
  isSessionOverlapError,
} from "@utils/utilsApi";
import { PagesID } from "@utils/utilsConfigurations";
import { useActivityDuration } from "./hooks/useActivityDuration";
import {
  getSessionTimeStatus,
  hasSessionOverlap,
  totalMinutesWith,
} from "./sessionChecks";
import { copyToClipboard, getSessionLink } from "./sessionLink";
import { SessionDraft } from "./SessionWizard/types";
import {
  EnrollableEdition,
  useRescheduleEnrollment,
} from "./hooks/useRescheduleEnrollment";
import RescheduleEnrollmentModal from "./components/RescheduleEnrollmentModal";
import WarningDialogCst from "@components/WarningDialogCst";
import RescheduleSessionModal from "./components/RescheduleSessionModal";
import ConfirmDialogCst from "@components/ConfirmDialogCst";
import BadgeBoxCst from "@components/BadgeBoxCst";
import { ListItemAction } from "@components/ListItemCardCst/types";
import {
  resolveEnrollmentStatusKey,
  enrollmentStatusVariant,
} from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/config/enrollmentStatus";
import { formatEnrollTypeLabel } from "@pages/Contents/V2/Corporate/Catalog/UsersAndGroups/config/enrollTypeMap";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import { ButtonTertiaryIcon } from "src/UI/ButtonTertiaryIcon";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import PageHeaderV2Cst from "@components/PageHeaderV2Cst";

const ENROLLED_PAGE_SIZE = 10;

type DetailTab = "sessions" | "register" | "enrolled-users";

const formatRange = (start?: number, end?: number): string => {
  if (!start || !end) return "";
  const s = dayjs(start).locale("it");
  const e = dayjs(end).locale("it");
  return `Dal ${s.format("D MMMM YYYY")} al ${e.format("D MMMM YYYY")}`;
};

// Same resolution used by the status badge, so disabled state matches what is shown.
const isEnrollmentCompleted = (u: EditionEnrolledUser): boolean =>
  !!u.status &&
  resolveEnrollmentStatusKey({ status: u.status }) === "completed";

const EditionDetailPage: React.FC = () => {
  const { t } = useTranslation("edition-detail");
  const { t: tEnroll } = useTranslation("users-and-groups");
  const theme = useTheme();
  const navigate = useNavigate();
  const { id: corporateId = "", idLO = "", editionId = "" } = useParams();
  const [searchParams] = useSearchParams();

  const kind: EditionKind =
    searchParams.get("kind") === "virtual" ? "virtual" : "physical";

  const initialTab: DetailTab = ((): DetailTab => {
    const t = searchParams.get("tab");
    return t === "register" || t === "enrolled-users" ? t : "sessions";
  })();

  const {
    detail,
    isLoading,
    mutate: refetchDetail,
  } = useEditionDetail(corporateId, editionId, kind);

  const { activityTitle } = useActivityInfo(corporateId, idLO);

  const breadcrumbLabels = useMemo(
    () => (activityTitle ? { activities: activityTitle } : undefined),
    [activityTitle],
  );
  const {
    sessions,
    isLoading: sessionsLoading,
    mutate: refetchSessions,
  } = useEditionSessions(corporateId, editionId, kind);

  const { submitSession, isSubmitting: isSessionSubmitting } =
    useSessionWizardSubmit(corporateId, editionId, kind);

  const { activityDuration } = useActivityDuration(
    corporateId,
    detail?.learning_objects?.id,
  );

  const [durationWarn, setDurationWarn] = useState<{
    resolve: (proceed: boolean) => void;
  } | null>(null);

  const submitSessionMapped = async (
    draft: SessionDraft,
    editSessionId?: string | number,
  ) => {
    try {
      await submitSession(draft, detail || {}, sessions, editSessionId);
    } catch (e: any) {
      throw new Error(
        isSessionOverlapError(e)
          ? formatSessionOccupiedError(e, t)
          : getAllErrorsAsString(e) || (e?.message ?? ""),
      );
    }
  };

  const confirmDurationIfNeeded = (
    draft: SessionDraft,
    editSessionId?: string | number,
  ): Promise<boolean> => {
    if (
      !activityDuration ||
      totalMinutesWith(draft, sessions, editSessionId) === activityDuration
    ) {
      return Promise.resolve(true);
    }
    return new Promise<boolean>((resolve) => setDurationWarn({ resolve }));
  };

  const editionContextLabel = () =>
    detail?.learning_objects?.label || detail?.edition_name || undefined;

  const handleEditSession = (session: EditionSession) => {
    setDetailSession(null);
    openSessionWizardModal({
      kind,
      corporateId,
      editionId,
      contextLabel: editionContextLabel(),
      editSession: session,
      publishDate: detail?.publish_date,
      onConfirm: async (draft) => {
        if (hasSessionOverlap(draft, sessions, session.id)) {
          throw new Error(String(t("session-already-exist-error")));
        }
        const proceed = await confirmDurationIfNeeded(draft, session.id);
        if (!proceed) return false;
        await submitSessionMapped(draft, session.id);
        refetchSessions();
        refetchDetail();
      },
    });
  };

  const [activeTab, setActiveTab] = useState<DetailTab>(initialTab);

  const [detailSession, setDetailSession] = useState<{
    session: EditionSession;
    index: number;
    tab: SessionDetailTab;
  } | null>(null);

  const [rescheduleSession, setRescheduleSession] = useState<{
    session: EditionSession;
    sessionNumber: number;
  } | null>(null);

  const [deleteSession, setDeleteSession] = useState<{
    session: EditionSession;
    sessionNumber: number;
  } | null>(null);

  const { deleteSession: triggerDeleteSession, isDeleting: isDeletingSession } =
    useDeleteSession(corporateId, editionId, kind);

  const handleCopySessionLink = async (session: EditionSession) => {
    const copied = await copyToClipboard(getSessionLink(session));
    openToast({
      severity: copied ? "success" : "error",
      message: String(
        t(copied ? "session.link-copied" : "session.link-copy-error"),
      ),
    });
  };

  const handleOpenRescheduleSession = (
    session: EditionSession,
    sessionNumber: number,
  ) => {
    setDetailSession(null);
    setRescheduleSession({ session, sessionNumber });
  };

  const handleConfirmRescheduleSession = async (
    startIso: string,
    endIso: string,
  ) => {
    if (!rescheduleSession) return;
    await submitSessionMapped(
      {
        ...(rescheduleSession.session as any),
        start_date: startIso,
        end_date: endIso,
      },
      rescheduleSession.session.id,
    );
    refetchSessions();
    refetchDetail();
    setRescheduleSession(null);
  };

  const handleConfirmDeleteSession = async () => {
    if (!deleteSession) return;
    try {
      await triggerDeleteSession(
        detail || {},
        sessions,
        deleteSession.session.id,
      );
      openToast({
        severity: "success",
        message: String(t("delete-session.success")),
      });
      refetchSessions();
      refetchDetail();
    } catch {
      openToast({
        severity: "error",
        message: String(t("delete-session.error")),
      });
    } finally {
      setDeleteSession(null);
    }
  };

  const { can } = usePermissions();
  // Tab "Utenti iscritti": editions.*.retrieve (già richiesto dalla pagina) +
  // learning.enrollment.read. Senza permesso la lista non viene caricata.
  const canViewEnrolledUsers = can(PERMISSIONS.enrollment.read);

  const [enrolledPage, setEnrolledPage] = useState(1);
  const [enrolledPageSize, setEnrolledPageSize] = useState(ENROLLED_PAGE_SIZE);
  const [enrolledSearch, setEnrolledSearch] = useState("");
  const [enrolledFieldFilters, setEnrolledFieldFilters] = useState<
    Record<string, any>
  >({});

  const {
    items: enrolledUsers,
    totalItems: enrolledTotal,
    totalPages: enrolledTotalPages,
    isLoading: enrolledLoading,
    mutate: refetchEnrolledUsers,
  } = useEditionEnrolledUsers({
    corporateId,
    editionId: canViewEnrolledUsers ? editionId : "",
    page: enrolledPage,
    pageSize: enrolledPageSize,
    search: enrolledSearch || undefined,
    fieldFilters: enrolledFieldFilters,
  });

  // Dettaglio visibile con editions.*.retrieve; senza editions.*.update è in
  // sola lettura (nessuna modifica di edizione e sessioni).
  const canViewEdition = can(editionPerm(kind, "retrieve"));
  const canUpdateEdition = can(editionPerm(kind, "update"));
  // P89 (allineare V2 a v1): Ripianifica ed Elimina iscrizione degli iscritti
  // all'edizione con update.enrollment.write (subscribed-users-editions-config).
  const canManageEnrollments = can(PERMISSIONS.enrollment.update);

  const { dataDetail: nonDeletableIds } = useFetchDataDetail(
    ServicesURL.getEditionsNotDeletable,
  );
  const isEditionNotDeletable = Array.isArray(nonDeletableIds)
    ? nonDeletableIds.includes(Number(editionId)) ||
      nonDeletableIds.includes(editionId)
    : false;

  const { deleteEdition, isDeleting: isDeletingEdition } =
    useDeleteEdition(kind);
  const [confirmDeleteEdition, setConfirmDeleteEdition] = useState(false);

  const { sendMail, isPreparing: isPreparingMail } = useEditionMailto({
    corporateId,
  });

  const handleSendEmail = async () => {
    const outcome = await sendMail({ editionId, kind });
    if (outcome === "URL_TOO_LONG") {
      openToast({
        severity: "warning",
        message: String(t("mail.url-too-long")),
      });
    } else if (outcome === "ERROR") {
      openToast({
        severity: "error",
        message: String(t("mail.error")),
      });
    }
  };

  const handleConfirmDeleteEdition = async () => {
    try {
      await deleteEdition({ editionId, corporateId });
      openToast({
        severity: "success",
        message: String(t("delete-edition.success")),
      });
      setConfirmDeleteEdition(false);
      navigate(-1);
    } catch {
      openToast({
        severity: "error",
        message: String(t("delete-edition.error")),
      });
      setConfirmDeleteEdition(false);
    }
  };

  const objectId = detail?.learning_objects?.id;

  const [rescheduleUser, setRescheduleUser] =
    useState<EditionEnrolledUser | null>(null);
  const [rescheduleEditions, setRescheduleEditions] = useState<
    EnrollableEdition[]
  >([]);
  const [isLoadingRescheduleEditions, setIsLoadingRescheduleEditions] =
    useState(false);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [deleteUser, setDeleteUser] = useState<EditionEnrolledUser | null>(
    null,
  );

  const {
    fetchEnrollableEditions,
    submitReschedule,
    awaitOutcome,
    isSubmitting: isReschedulingPatch,
  } = useRescheduleEnrollment();
  const { deleteEnrollment, isDeleting } = useDeleteEditionEnrollment();
  const { downloadEnrolledUsers, isDownloading: isDownloadingEnrolled } =
    useDownloadEditionEnrolledUsers();

  const handleDownloadEnrolled = async () => {
    openToast({
      severity: "info",
      message: String(t("enrolled-users.download-in-progress")),
    });
    try {
      await downloadEnrolledUsers({
        corporateId,
        editionId,
        totalItems: enrolledTotal,
        fieldFilters: enrolledFieldFilters,
      });
      openToast({
        severity: "success",
        message: String(t("enrolled-users.download-success")),
      });
    } catch {
      openToast({
        severity: "error",
        message: String(t("enrolled-users.download-error")),
      });
    }
  };

  const handleOpenReschedule = async (u: EditionEnrolledUser) => {
    if (
      !canManageEnrollments ||
      !objectId ||
      !u.idUser ||
      isEnrollmentCompleted(u)
    )
      return;
    setRescheduleUser(u);
    setRescheduleEditions([]);
    setIsLoadingRescheduleEditions(true);
    try {
      const list = await fetchEnrollableEditions({
        corporateId,
        objectId,
        studentEid: u.idUser,
      });
      if (list.length === 0) {
        openToast({
          severity: "warning",
          message: String(t("reschedule.no-editions-available")),
        });
        setRescheduleUser(null);
        return;
      }
      setRescheduleEditions(list);
    } catch {
      openToast({
        severity: "error",
        message: String(t("reschedule.fetch-error")),
      });
      setRescheduleUser(null);
    } finally {
      setIsLoadingRescheduleEditions(false);
    }
  };

  const handleCloseReschedule = () => {
    if (isReschedulingPatch) return;
    setRescheduleUser(null);
    setRescheduleEditions([]);
  };

  const handleConfirmReschedule = async (newEditionId: number) => {
    if (!rescheduleUser?.idUser || !objectId) return;
    const userId = rescheduleUser.idUser;
    try {
      await submitReschedule({
        corporateId,
        objectId,
        studentEid: userId,
        newEditionId,
      });
    } catch (e: any) {
      if (isSessionOverlapError(e)) {
        setRescheduleError(formatSessionOccupiedError(e, t));
        return;
      }
      openToast({
        severity: "error",
        message: String(t("reschedule.error")),
      });
      setRescheduleUser(null);
      setRescheduleEditions([]);
      return;
    }

    setRescheduleUser(null);
    setRescheduleEditions([]);
    openToast({
      severity: "info",
      message: String(t("reschedule.submitted")),
    });

    const outcome = await awaitOutcome({
      corporateId,
      objectId,
      studentEid: userId,
      newEditionId,
    });

    if (outcome.status === "COMPLETE") {
      openToast({
        severity: "success",
        message: String(t("reschedule.success")),
      });
    } else if (outcome.status === "TIMEOUT") {
      openToast({
        severity: "error",
        message: String(t("reschedule.timeout")),
      });
    } else {
      openToast({
        severity: "error",
        message: outcome.errorMessage || String(t("reschedule.error")),
      });
    }
    refetchEnrolledUsers();
    refetchDetail();
  };

  const handleConfirmDelete = async () => {
    if (
      !canManageEnrollments ||
      !deleteUser?.idUser ||
      !objectId ||
      isEnrollmentCompleted(deleteUser)
    )
      return;
    try {
      await deleteEnrollment({
        corporateId,
        objectId,
        studentEid: deleteUser.idUser,
        editionId,
      });
      openToast({
        severity: "success",
        message: String(t("delete-enrollment.success")),
      });
      refetchEnrolledUsers();
      refetchDetail();
    } catch {
      openToast({
        severity: "error",
        message: String(t("delete-enrollment.error")),
      });
    } finally {
      setDeleteUser(null);
    }
  };

  const tabs = [
    { key: "sessions", label: String(t("tabs.sessions")) },
    ...(can(PERMISSIONS.registry.read)
      ? [{ key: "register", label: String(t("tabs.register")) }]
      : []),
    ...(canViewEnrolledUsers
      ? [{ key: "enrolled-users", label: String(t("tabs.enrolled-users")) }]
      : []),
  ];
  const visibleTab: DetailTab =
    activeTab === "enrolled-users" && !canViewEnrolledUsers
      ? "sessions"
      : activeTab;

  const occupiedPlaces = detail?.occupied_places ?? 0;
  const totalPlaces = detail?.max_capacity ?? detail?.total_places ?? 0;
  const hasPlaces = !!occupiedPlaces || !!totalPlaces;

  const headerIconBtn = (
    icon: React.ReactNode,
    label: string,
    onClick: () => void,
    disabled?: boolean,
  ) => (
    <ButtonTertiaryIcon
      onClick={onClick}
      aria-label={label}
      disabled={disabled}
      startIcon={icon}
    />
  );

  if (!canViewEdition) return <Navigate to="/403" replace />;

  return (
    <AnimationFadeIn>
      <Box sx={{ pr: 3 }}>
        <PageHeaderV2Cst>
          <Breadcrumb sticky={false} labelOverrides={breadcrumbLabels} />

          {!!idLO && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                mb: 1,
                color: theme.palette.exploreCardText,
              }}
            >
              <GraduationCap size={20} />
              {activityTitle && (
                <>
                  <Typography sx={{ fontSize: "1rem", fontWeight: 600 }}>
                    {activityTitle}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderColor: theme.palette.systemSecondary01,
                      my: 0.25,
                    }}
                  />
                </>
              )}
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {t("activity-id", { id: idLO })}
              </Typography>
            </Box>
          )}
          {!isLoading && (
            <Stack flexDirection="column" gap={"1.5rem"}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "1rem",
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
                >
                  <ButtonTertiaryIcon
                    onClick={() => navigate(-1)}
                    aria-label="back"
                    sx={{
                      paddingLeft: "0",
                    }}
                  >
                    <ArrowLeft size={"1.25rem"} />
                  </ButtonTertiaryIcon>
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: theme.palette.exploreCardText,
                    }}
                  >
                    {t("edition-title", { id: editionId })}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  {/* P83 (allineare V2 a v1): la pagina di modifica si apre con
                      editions.*.retrieve e senza update è in sola lettura,
                      come l'azione Modifica di v1 e la voce Modifica dell'elenco. */}
                  <PermissionGate
                    permission={editionPerm(kind, "retrieve")}
                    mode="disable"
                  >
                    {headerIconBtn(
                      canUpdateEdition ? (
                        <Pencil size={18} />
                      ) : (
                        <Eye size={18} />
                      ),
                      canUpdateEdition ? "edit" : "view",
                      () =>
                        navigate(
                          { pathname: "edit", search: `?kind=${kind}` },
                          { relative: "path" },
                        ),
                    )}
                  </PermissionGate>
                  <PermissionGate
                    permission={editionPerm(kind, "update")}
                    mode="disable"
                  >
                    {headerIconBtn(
                      <Trash2 size={18} />,
                      "delete",
                      () => setConfirmDeleteEdition(true),
                      isEditionNotDeletable,
                    )}
                  </PermissionGate>
                </Box>
              </Box>
              <Stack flexDirection="column" gap={"0.5rem"}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                    color: theme.palette.exploreCardText,
                  }}
                >
                  <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                    {`${t("code-label")}: ${detail?.edition_code || "-"}`}
                  </Typography>
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                      borderColor: theme.palette.systemSecondary01,
                      my: 0.25,
                    }}
                  />
                  <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                    {`${t("name-label")}: ${detail?.edition_name || "-"}`}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: theme.palette.exploreCardText,
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <Calendar size="1.25rem" />{" "}
                  {formatRange(detail?.start_date, detail?.end_date)}
                </Typography>

                {hasPlaces && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: theme.palette.systemSecondary01,
                    }}
                  >
                    <Users size={14} />
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600 }}>
                      {t("places", {
                        capacity: `${occupiedPlaces}/${totalPlaces}`,
                        interpolation: { escapeValue: false },
                      })}
                    </Typography>
                  </Box>
                )}
              </Stack>

              {!!detail?.extra_info && (
                <DetailAccordion
                  title={String(t("extra-info"))}
                  defaultExpanded
                  sxAccordion={{
                    "& .MuiAccordionSummary-root": {
                      minHeight: "2.2rem",
                      position: "relative",
                      "&.Mui-expanded::after": {
                        content: '""',
                        position: "absolute",
                        left: "0.5rem",
                        right: "0.5rem",
                        bottom: 0,
                        height: "1px",
                        backgroundColor: theme.palette.divider,
                      },
                    },
                    "& .MuiAccordionSummary-content": {
                      mt: "0.25rem !important",
                      mb: "0.25rem !important",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      whiteSpace: "pre-line",
                      wordBreak: "break-word",
                      maxHeight: "12.5rem",
                      overflowY: "auto",
                    }}
                  >
                    {detail.extra_info}
                  </Typography>
                </DetailAccordion>
              )}

              <Box sx={{ display: "flex", gap: 1.5 }}>
                {/* D07: editions.*.enrollment OPPURE editions.*.update (come v1) */}
                <PermissionGate
                  anyOf={[
                    editionPerm(kind, "enrollment"),
                    editionPerm(kind, "update"),
                  ]}
                  mode="disable"
                >
                  <ButtonPrimary
                    onClick={() =>
                      corporateId &&
                      openEditionEnrollmentModal({
                        editionId: Number(editionId),
                        objectId: Number(idLO),
                        corporateId,
                        objectTitle:
                          detail?.learning_objects?.label ||
                          detail?.edition_name ||
                          undefined,
                        onSuccess: () => {
                          refetchEnrolledUsers();
                          setActiveTab("enrolled-users");
                        },
                      })
                    }
                  >
                    {t("actions.enroll")}
                  </ButtonPrimary>
                </PermissionGate>
                {/* D08: come v1, Invia email con learning.enrollment.read */}
                <PermissionGate
                  permission={PERMISSIONS.enrollment.read}
                  mode="disable"
                >
                  <ButtonSecondary
                    onClick={handleSendEmail}
                    disabled={isPreparingMail}
                  >
                    {t("actions.send-email")}
                  </ButtonSecondary>
                </PermissionGate>
              </Box>

              <TabsCst
                tabs={tabs}
                activeKey={visibleTab}
                onChange={(k) => setActiveTab(k as DetailTab)}
              />
            </Stack>
          )}
        </PageHeaderV2Cst>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack flexDirection="column" gap={"1.5rem"} sx={{ mt: "1.5rem" }}>
            {visibleTab === "sessions" && (
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "20px",
                      fontWeight: 700,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {t("tabs.sessions")}
                  </Typography>
                </Box>

                {sessionsLoading ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                ) : sessions.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 4,
                      color: theme.palette.text.primary,
                      fontSize: "13px",
                      borderRadius: "12px",
                      border: `1px dashed ${theme.palette.divider}`,
                    }}
                  >
                    {t("session.empty")}
                  </Box>
                ) : (
                  sessions.map((session, index) => {
                    const sessionNumber = index + 1;
                    const isEditionPublished =
                      !!detail?.publish_date &&
                      dayjs().isAfter(dayjs(detail.publish_date));
                    const isRescheduleLocked =
                      getSessionTimeStatus(session) !== "future";
                    const menuActions: ListItemAction[] = [
                      {
                        label: String(t("session.actions.reschedule")),
                        icon: <CalendarClock size={16} />,
                        disabled: !canUpdateEdition || isRescheduleLocked,
                        onClick: () =>
                          handleOpenRescheduleSession(session, sessionNumber),
                      },
                      {
                        label: String(t("session.actions.edit")),
                        icon: <Pencil size={16} />,
                        disabled: !canUpdateEdition,
                        onClick: () => handleEditSession(session),
                      },
                      {
                        label: String(t("session.actions.delete")),
                        icon: <Trash2 size={16} />,
                        disabled: isEditionPublished || !canUpdateEdition,
                        onClick: () =>
                          setDeleteSession({ session, sessionNumber }),
                      },
                      ...(kind === "virtual"
                        ? [
                            {
                              label: String(t("session.actions.copy-link")),
                              icon: <Link2 size={16} />,
                              disabled: !getSessionLink(session),
                              onClick: () => handleCopySessionLink(session),
                            },
                          ]
                        : []),
                    ];
                    return (
                      <SessionCard
                        key={session.id}
                        session={session}
                        kind={kind}
                        onVerifyInvites={() =>
                          setDetailSession({
                            session,
                            index,
                            tab: "verify-invites",
                          })
                        }
                        onDetails={() =>
                          setDetailSession({ session, index, tab: "details" })
                        }
                        menuActions={menuActions}
                      />
                    );
                  })
                )}
              </Box>
            )}

            {visibleTab === "register" && (
              <RegistryTab corporateId={corporateId} editionId={editionId} />
            )}

            {visibleTab === "enrolled-users" && (
              <EnrollmentListShell
                search={enrolledSearch}
                onSearchSubmit={(v) => {
                  setEnrolledSearch(v);
                  setEnrolledPage(1);
                }}
                hideSearch
                actionsSlot={
                  <PermissionGate
                    permission={PERMISSIONS.enrollment.update}
                    mode="disable"
                  >
                    <ButtonTertiary
                      size="md"
                      startIcon={<Download size={16} />}
                      onClick={handleDownloadEnrolled}
                      disabled={enrolledLoading || isDownloadingEnrolled}
                      sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "none" },
                      }}
                    >
                      {t("enrolled-users.download")}
                    </ButtonTertiary>
                  </PermissionGate>
                }
                filtersPageId={PagesID["enrollment.subscribed.edition"]}
                fieldFilters={enrolledFieldFilters}
                onApplyFilters={(next) => {
                  setEnrolledFieldFilters(next);
                  setEnrolledPage(1);
                }}
                filtersExcludeKeys={["editionId"]}
                isLoading={enrolledLoading}
                isEmpty={enrolledUsers.length === 0}
                emptyLabel={String(t("enrolled-users.empty"))}
                page={enrolledPage}
                pageSize={enrolledPageSize}
                totalItems={enrolledTotal}
                totalPages={enrolledTotalPages}
                onPageChange={setEnrolledPage}
                onPageSizeChange={(next) => {
                  setEnrolledPageSize(next);
                  setEnrolledPage(1);
                }}
              >
                {enrolledUsers.map((u) => (
                  <Box
                    key={String(getEditionUserId(u))}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      padding: "14px 16px",
                      borderRadius: "10px",
                      border: `1px solid ${theme.palette.divider}`,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 700,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {getEditionUserName(u)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: theme.palette.text.primary,
                        }}
                      >
                        {`ID: ${getEditionUserId(u)}${
                          u.externalId ? ` | Ext. ID: ${u.externalId}` : ""
                        }${u.email ? ` | ${u.email}` : ""}`}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-end",
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      {(u.status || u.enrollType) && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                            justifyContent: "flex-end",
                          }}
                        >
                          {u.enrollType && (
                            <BadgeBoxCst>
                              {formatEnrollTypeLabel(u.enrollType)}
                            </BadgeBoxCst>
                          )}
                          {u.status &&
                            (() => {
                              const statusKey = resolveEnrollmentStatusKey({
                                status: u.status,
                              });
                              return (
                                <BadgeBoxCst
                                  variant={enrollmentStatusVariant(statusKey)}
                                >
                                  {String(tEnroll(`card.status.${statusKey}`))}
                                </BadgeBoxCst>
                              );
                            })()}
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Tooltip
                          title={
                            isEnrollmentCompleted(u)
                              ? String(
                                  t(
                                    "enrolled-users.reschedule-disabled-completed",
                                  ),
                                )
                              : ""
                          }
                        >
                          <span>
                            <ButtonSecondary
                              size="md"
                              onClick={() => handleOpenReschedule(u)}
                              disabled={
                                !canManageEnrollments ||
                                isEnrollmentCompleted(u)
                              }
                            >
                              {t("enrolled-users.reschedule")}
                            </ButtonSecondary>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            isEnrollmentCompleted(u)
                              ? String(
                                  t("enrolled-users.delete-disabled-completed"),
                                )
                              : ""
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() => setDeleteUser(u)}
                              disabled={
                                !canManageEnrollments ||
                                isEnrollmentCompleted(u)
                              }
                              aria-label="delete enrollment"
                              sx={{ color: theme.palette.primary.main }}
                            >
                              <Trash2 size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Box>
                ))}
              </EnrollmentListShell>
            )}
          </Stack>
        )}

        {detailSession && (
          <SessionDetailModal
            session={detailSession.session}
            kind={kind}
            corporateId={corporateId}
            editionId={editionId}
            sessionNumber={detailSession.index + 1}
            contextLabel={[
              detail?.learning_objects?.label,
              detail?.edition_name,
            ]
              .filter(Boolean)
              .join(" - ")}
            initialTab={detailSession.tab}
            onEditSession={
              canUpdateEdition
                ? () => handleEditSession(detailSession.session)
                : undefined
            }
            onOpenReschedule={
              canUpdateEdition
                ? () =>
                    handleOpenRescheduleSession(
                      detailSession.session,
                      detailSession.index + 1,
                    )
                : undefined
            }
            onDeleteSession={
              canUpdateEdition
                ? () => {
                    const session = detailSession.session;
                    const sessionNumber = detailSession.index + 1;
                    setDetailSession(null);
                    setDeleteSession({ session, sessionNumber });
                  }
                : undefined
            }
            deleteDisabled={
              !!detail?.publish_date &&
              dayjs().isAfter(dayjs(detail.publish_date))
            }
            onClose={() => setDetailSession(null)}
          />
        )}

        {rescheduleSession && (
          <RescheduleSessionModal
            open
            session={rescheduleSession.session}
            sessionNumber={rescheduleSession.sessionNumber}
            contextLabel={editionContextLabel()}
            publishDate={detail?.publish_date}
            otherSessions={sessions.filter(
              (s) => s.id !== rescheduleSession.session.id,
            )}
            isSubmitting={isSessionSubmitting}
            onClose={() => setRescheduleSession(null)}
            onConfirm={handleConfirmRescheduleSession}
          />
        )}

        {deleteSession && (
          <ConfirmDialogCst
            open
            title={String(t("delete-session.title"))}
            description={String(
              t("delete-session.description", {
                number: deleteSession.sessionNumber,
              }),
            )}
            confirmLabel={String(t("delete-session.confirm"))}
            cancelLabel={String(t("delete-session.cancel"))}
            confirmVariant="danger"
            isSubmitting={isDeletingSession}
            submittingLabel={String(t("delete-session.deleting"))}
            onClose={() => setDeleteSession(null)}
            onConfirm={handleConfirmDeleteSession}
          />
        )}

        {durationWarn && (
          <ConfirmDialogCst
            open
            title={String(t("duration-warning.title"))}
            description={String(t("duration-warning.description"))}
            confirmLabel={String(t("duration-warning.confirm"))}
            cancelLabel={String(t("duration-warning.cancel"))}
            confirmVariant="primary"
            onClose={() => {
              const r = durationWarn.resolve;
              setDurationWarn(null);
              r(false);
            }}
            onConfirm={() => {
              const r = durationWarn.resolve;
              setDurationWarn(null);
              r(true);
            }}
          />
        )}

        {rescheduleUser && (
          <RescheduleEnrollmentModal
            open
            editions={rescheduleEditions}
            isLoadingEditions={isLoadingRescheduleEditions}
            isSubmitting={isReschedulingPatch}
            onClose={handleCloseReschedule}
            onConfirm={handleConfirmReschedule}
          />
        )}

        <WarningDialogCst
          open={!!rescheduleError}
          title={String(t("reschedule.error-title"))}
          description={rescheduleError ?? ""}
          closeLabel={String(t("reschedule.error-close"))}
          onClose={() => setRescheduleError(null)}
        />

        {deleteUser && (
          <ConfirmDialogCst
            open
            title={String(t("delete-enrollment.title"))}
            description={String(
              t("delete-enrollment.description-user", {
                user: getEditionUserName(deleteUser),
              }),
            )}
            confirmLabel={String(t("delete-enrollment.confirm"))}
            cancelLabel={String(t("delete-enrollment.cancel"))}
            confirmVariant="danger"
            isSubmitting={isDeleting}
            submittingLabel={String(t("delete-enrollment.deleting"))}
            onClose={() => setDeleteUser(null)}
            onConfirm={handleConfirmDelete}
          />
        )}

        {confirmDeleteEdition && (
          <ConfirmDialogCst
            open
            title={String(t("delete-edition.title"))}
            description={String(
              t("delete-edition.description", {
                name:
                  detail?.edition_name ||
                  detail?.edition_code ||
                  `#${editionId}`,
              }),
            )}
            confirmLabel={String(t("delete-edition.confirm"))}
            cancelLabel={String(t("delete-edition.cancel"))}
            confirmVariant="danger"
            isSubmitting={isDeletingEdition}
            submittingLabel={String(t("delete-edition.deleting"))}
            onClose={() => setConfirmDeleteEdition(false)}
            onConfirm={handleConfirmDeleteEdition}
          />
        )}
      </Box>
    </AnimationFadeIn>
  );
};

export default EditionDetailPage;
